import { useMemo, useState } from 'react'
import { Button } from '@agms/ui'
import { useAuth } from '../hooks/useAuth'
import { useAppointmentAssignees, useMyAppointments } from '../hooks/useAppointments'
import { AppointmentCalendar } from '../components/appointments/AppointmentCalendar'
import { AppointmentDialog } from '../components/appointments/AppointmentDialog'
import { GoogleCalendarConnectionCard } from '../components/GoogleCalendarConnectionCard'
import { PageHeader, SurfaceCard } from '../components/ui'
import {
  canBookSlot,
  nextBookableSlot,
  periodLabel,
  shiftCursor,
  startOfDay,
  visibleRange,
  type CalendarView,
} from '../lib/appointmentCalendar'
import { dateTimeLocalOn } from '../lib/formatAppointmentWhen'
import {
  canCreateAppointment,
  canViewAllAppointments,
} from '../lib/appointmentForm'
import type { Appointment } from '../lib/api'

const VIEWS: CalendarView[] = ['day', 'week', 'month']

function initialCalendarView(): CalendarView {
  if (typeof window !== 'undefined' && window.innerWidth < 768) return 'day'
  return 'week'
}

type DialogState =
  | { mode: 'create'; startsAt: string }
  | { mode: 'appointment'; appointment: Appointment }
  | null

export function MyAppointmentsPage() {
  const { user } = useAuth()
  const canViewAll = canViewAllAppointments(user)
  const canCreate = canCreateAppointment(user)
  const [view, setView] = useState<CalendarView>(initialCalendarView)
  const [cursor, setCursor] = useState(() => startOfDay(new Date()))
  const [assignedUserId, setAssignedUserId] = useState('')
  const [dialog, setDialog] = useState<DialogState>(null)
  const assigneesQuery = useAppointmentAssignees(canViewAll)
  const range = useMemo(() => visibleRange(cursor, view), [cursor, view])
  const query = useMyAppointments({
    from: range.start.toISOString(),
    to: range.end.toISOString(),
    ...(canViewAll && assignedUserId ? { assigned_user_id: assignedUserId } : {}),
  })

  const title = canViewAll ? 'Appointments' : 'My appointments'
  const previousLabel = view === 'day' ? 'Previous day' : view === 'week' ? 'Previous week' : 'Previous month'
  const nextLabel = view === 'day' ? 'Next day' : view === 'week' ? 'Next week' : 'Next month'

  const openAt = (day: Date, hours: number, minutes: number) => {
    const slot = canBookSlot(day, hours, minutes) ? { day, hours, minutes } : nextBookableSlot()
    setDialog({ mode: 'create', startsAt: dateTimeLocalOn(slot.day, slot.hours, slot.minutes) })
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={title}
        subtitle="Times are shown in your time zone."
        action={canCreate ? (
          <Button type="button" onClick={() => openAt(cursor, 9, 0)}>New appointment</Button>
        ) : undefined}
      />

      <GoogleCalendarConnectionCard />

      <SurfaceCard padding="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button type="button" variant="secondary" onClick={() => setCursor(shiftCursor(cursor, view, -1))}>
              {previousLabel}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setCursor(startOfDay(new Date()))}>
              Today
            </Button>
            <Button type="button" variant="secondary" onClick={() => setCursor(shiftCursor(cursor, view, 1))}>
              {nextLabel}
            </Button>
          </div>
          <div className="flex items-center gap-2" role="group" aria-label="Calendar view">
            {VIEWS.map((option) => (
              <Button
                key={option}
                type="button"
                variant={view === option ? 'primary' : 'secondary'}
                aria-pressed={view === option}
                onClick={() => setView(option)}
              >
                {option === 'day' ? 'Day' : option === 'week' ? 'Week' : 'Month'}
              </Button>
            ))}
          </div>
          {canViewAll && (
            <label className="text-sm font-medium text-slate-700">
              Rep
              <select
                aria-label="Filter by rep"
                value={assignedUserId}
                onChange={(event) => setAssignedUserId(event.target.value)}
                className="ml-2 rounded-md border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="">All reps</option>
                {(assigneesQuery.data ?? []).map((assignee) => (
                  <option key={assignee.id} value={assignee.id}>{assignee.name}</option>
                ))}
              </select>
            </label>
          )}
        </div>
        <p className="mt-3 text-sm font-semibold text-slate-800" aria-live="polite">
          {periodLabel(cursor, view)}
        </p>
      </SurfaceCard>

      {query.isLoading && <p className="text-sm text-slate-500">Loading appointments...</p>}
      {query.error && <p className="text-sm text-red-600" role="alert">Unable to load appointments.</p>}

      <AppointmentCalendar
        view={view}
        cursor={cursor}
        range={range}
        appointments={query.data ?? []}
        canCreate={canCreate}
        canViewAll={canViewAll}
        user={user ?? null}
        onOpen={(appointment) => setDialog({ mode: 'appointment', appointment })}
        onCreateAt={openAt}
      />

      {dialog?.mode === 'create' && (
        <AppointmentDialog
          mode="create"
          initialStartsAt={dialog.startsAt}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.mode === 'appointment' && (
        <AppointmentDialog
          mode="appointment"
          appointment={dialog.appointment}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  )
}
